import { cellKey, cellToBox } from '../core/coordinates';
import { isMine, DEFAULT_MINE_PROBABILITY } from '../core/mineGenerator';
import { getAdjacentMineCount } from './adjacent';
import { CellState } from '../core/cell';
import { BoxStatus } from '../core/box';

const OFFSETS = [
  [-1, -1], [0, -1], [1, -1],
  [-1,  0],          [1,  0],
  [-1,  1], [0,  1], [1,  1],
];

export interface FloodFillParams {
  seed: string;
  startX: number;
  startY: number;
  getCellState: (x: number, y: number) => CellState;
  getBoxStatus: (boxX: number, boxY: number) => BoxStatus;
  mineProbability?: number;
}

/**
 * Performs flood-fill auto-reveal starting from (startX, startY).
 * Returns an array of world coordinates {x, y} for newly revealed cells.
 */
export function performFloodFill(params: FloodFillParams): { x: number; y: number }[] {
  const { seed, startX, startY, getCellState, getBoxStatus, mineProbability = DEFAULT_MINE_PROBABILITY } = params;

  const revealedCells: { x: number; y: number }[] = [];
  const visited = new Set<string>();
  const queue: [number, number][] = [[startX, startY]];

  const startKey = cellKey(startX, startY);
  visited.add(startKey);

  while (queue.length > 0) {
    const [cx, cy] = queue.shift()!;

    // Check if cell's box is interactive (ACTIVE)
    const { boxX, boxY } = cellToBox(cx, cy);
    const boxStatus = getBoxStatus(boxX, boxY);
    if (boxStatus === 'BUSTED' || boxStatus === 'COMPLETED') {
      continue;
    }

    const state = getCellState(cx, cy);
    if (state === 'FLAGGED' || state === 'REVEALED') {
      continue;
    }

    // Must be a safe cell
    if (isMine(seed, cx, cy, mineProbability)) {
      continue;
    }

    revealedCells.push({ x: cx, y: cy });

    const adjacentMines = getAdjacentMineCount(seed, cx, cy, mineProbability);
    if (adjacentMines === 0) {
      // Expand to 8 neighbors
      for (const [dx, dy] of OFFSETS) {
        const nx = cx + dx;
        const ny = cy + dy;
        const nKey = cellKey(nx, ny);

        if (!visited.has(nKey)) {
          visited.add(nKey);
          queue.push([nx, ny]);
        }
      }
    }
  }

  return revealedCells;
}
